import {type RefObject, useCallback, useEffect, useId, useLayoutEffect, useRef, useState} from 'react'
import {announce} from '@github-ui/aria-live'
import CircleOcticon from '@github-ui/circle-octicon'
import {AlertIcon, ShieldCheckIcon, ShieldIcon} from '@primer/octicons-react'
import {Link} from '@primer/react'
import {useQueryClient} from '@github-ui/react-query'
import {
  PROOF_OF_PRESENCE_AUTHENTICATION_FAILED_EVENT,
  PROOF_OF_PRESENCE_AUTHENTICATION_SUCCEEDED_EVENT,
  type ProofOfPresenceAuthenticationEventDetail,
} from '@github-ui/pull-requests/constants'
import {MergeBoxSectionHeader} from './common/MergeBoxSectionHeader'
import {useMergeBoxPageDataQueryKey} from '../../page-data/loaders/use-merge-box-page-data'
import type {ProofOfPresenceInitiationPayload, ProofOfPresenceRequirement} from '../../types'
import styles from './ProofOfPresenceSection.module.css'

const HEADER_ICON_SIZE = 32

const DOCS_URL = 'https://docs.github.com/authentication'

interface ProofOfPresenceSectionProps {
  requirement: ProofOfPresenceRequirement
}

type ProofOfPresenceMetadata = ProofOfPresenceRequirement['metadata']

const POP_TAB_NAME = 'pop-reauth'
// A new merge-box launch must release the previous merge-box attempt.
let cancelActiveInitiation: (() => void) | undefined

function buildInitiationForm(initiation: ProofOfPresenceInitiationPayload): HTMLFormElement {
  const form = document.createElement('form')
  form.method = 'post'
  form.action = initiation.action
  form.hidden = true

  const fields: Record<string, string> = {
    authenticity_token: initiation.csrfToken ?? '',
    purpose: 'ref',
    pull_request_id: initiation.pullRequestId,
    return_to: initiation.returnTo,
  }
  if (initiation.requiredLevel) {
    fields.required_pop_level = initiation.requiredLevel
  }

  for (const [name, value] of Object.entries(fields)) {
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = name
    input.value = value
    form.append(input)
  }

  return form
}

/**
 * Starts the proof of presence round trip in a new tab and reports whether it
 * completed, failed, or was dismissed.
 *
 * The form is appended to `document.body` because the merge box renders inside the pull
 * request page's own form, and the parser drops nested forms.
 *
 * The tab comes from the named `target`, while `window.open` provides a handle for
 * noticing a dismissed tab. If the browser blocks that tab, the form falls back to a
 * full-page submission.
 *
 * `display=popup` opts into the shared SSO terminal, which signals
 * `window.opener.external.ssoComplete` and closes — hence no `rel="noopener"`. The
 * signal is an invalidation hint, never proof: we always re-read from the server.
 */
type InitiationResult =
  | 'completed'
  | 'dismissed'
  | 'failed'
  | NonNullable<ProofOfPresenceAuthenticationEventDetail['error']>

function reportAuthenticationFailure(
  pullRequestPath: string,
  requiredLevel: ProofOfPresenceInitiationPayload['requiredLevel'],
  returnFocusTarget: HTMLButtonElement | null,
  error?: ProofOfPresenceAuthenticationEventDetail['error'],
) {
  const detail: ProofOfPresenceAuthenticationEventDetail = {
    pullRequestPath,
    requiredLevel,
    returnFocusTarget,
    ...(error ? {error} : {}),
  }
  window.dispatchEvent(new CustomEvent(PROOF_OF_PRESENCE_AUTHENTICATION_FAILED_EVENT, {detail}))
}

function reportAuthenticationSuccess(pullRequestPath: string) {
  const detail: ProofOfPresenceAuthenticationEventDetail = {pullRequestPath}
  window.dispatchEvent(new CustomEvent(PROOF_OF_PRESENCE_AUTHENTICATION_SUCCEEDED_EVENT, {detail}))
}

function launchInitiationTab(
  initiation: ProofOfPresenceInitiationPayload,
  signal: AbortSignal,
): Promise<InitiationResult> {
  if (signal.aborted) return Promise.resolve('dismissed')
  cancelActiveInitiation?.()

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, compat/compat -- the bridge the shipped SSO terminal signals through.
  const external: any = window.external
  if (external?.ssoComplete) {
    announce('Authentication is already in progress. Complete it before starting another attempt.')
    return Promise.resolve('dismissed')
  }

  const form = buildInitiationForm(initiation)
  const display = document.createElement('input')
  display.type = 'hidden'
  display.name = 'display'
  display.value = 'popup'
  form.append(display)

  const tabName = `${POP_TAB_NAME}-${crypto.randomUUID()}`
  const tab = window.open('', tabName)

  document.body.append(form)

  if (tab && !external) {
    tab.close()
    form.remove()
    return Promise.resolve('failed')
  }

  let cancelAttempt: (() => void) | undefined
  const result = new Promise<InitiationResult>(resolve => {
    let settled = false
    let closedPoll: number | undefined
    const previousHandler = external?.ssoComplete
    const finish = (outcome: InitiationResult) => {
      if (settled) return
      settled = true
      if (external?.ssoComplete === onComplete) external.ssoComplete = previousHandler ?? null
      if (closedPoll !== undefined) window.clearInterval(closedPoll)
      signal.removeEventListener('abort', cancel)
      if (cancelActiveInitiation === cancel) cancelActiveInitiation = undefined
      tab?.close()
      resolve(outcome)
    }
    const onComplete = (data: {error?: unknown}) => {
      const error = data?.error
      if (error === 'pop_actor_mismatch' || error === 'pop_insufficient_auth_strength') {
        finish(error)
      } else {
        finish(error ? 'failed' : 'completed')
      }
    }
    const cancel = () => finish('dismissed')

    cancelAttempt = cancel
    cancelActiveInitiation = cancel
    signal.addEventListener('abort', cancel, {once: true})

    if (tab) {
      form.target = tabName
      external.ssoComplete = onComplete
      closedPoll = window.setInterval(() => {
        if (tab.closed) finish('dismissed')
      }, 500)
    } else {
      display.remove()
      form.removeAttribute('target')
    }

    form.submit()
    if (tab) form.remove()
  })

  return result.finally(() => {
    cancelAttempt?.()
    form.remove()
  })
}

/**
 * The satisfied state: a green shield with a checkmark naming the user whose proof
 * satisfied the requirement. The Authenticate control is withdrawn.
 */
const VerifiedHeader = ({
  verifiedBy,
  headerId,
}: {
  verifiedBy: ProofOfPresenceMetadata['verifiedBy']
  headerId: string
}) => (
  <MergeBoxSectionHeader
    headerId={headerId}
    title="Proof of presence verified"
    subtitle={verifiedBy ? `This requirement was satisfied by ${verifiedBy}` : undefined}
    icon={
      <CircleOcticon
        className={styles.satisfiedIcon}
        icon={() => <ShieldCheckIcon size={16} />}
        size={HEADER_ICON_SIZE}
      />
    }
  />
)

/**
 * The unsatisfied state: a neutral shield and the blocking message. The Authenticate
 * control is offered only to a viewer who can satisfy the requirement.
 */
const RequiredHeader = ({
  summary,
  message,
  initiation,
  isUnavailable,
  headerId,
  authenticateButtonRef,
  onBeforeUnmount,
  pullRequestPath,
}: {
  summary: ProofOfPresenceMetadata['reason']['summary']
  message: ProofOfPresenceRequirement['message']
  initiation: ProofOfPresenceMetadata['initiation']
  isUnavailable: boolean
  headerId: string
  authenticateButtonRef: RefObject<HTMLButtonElement | null>
  onBeforeUnmount: () => void
  pullRequestPath: ProofOfPresenceRequirement['pullRequestPath']
}) => {
  const mergeBoxQueryKey = useMergeBoxPageDataQueryKey()
  const queryClient = useQueryClient()
  const authenticationActiveRef = useRef(true)
  const authenticationInFlightRef = useRef(false)
  const authenticationControllerRef = useRef<AbortController | null>(null)
  const [isAuthenticating, setIsAuthenticating] = useState(false)

  useLayoutEffect(() => {
    authenticationActiveRef.current = true
    return () => {
      onBeforeUnmount()
      authenticationActiveRef.current = false
      authenticationControllerRef.current?.abort()
    }
  }, [onBeforeUnmount])

  async function authenticate(initiationPayload: ProofOfPresenceInitiationPayload, signal: AbortSignal) {
    const result = await launchInitiationTab(initiationPayload, signal)
    if (!authenticationActiveRef.current) return
    if (result === 'dismissed') return
    if (result !== 'completed') {
      reportAuthenticationFailure(
        pullRequestPath,
        initiationPayload.requiredLevel,
        authenticateButtonRef.current,
        result === 'failed' ? undefined : result,
      )
      return
    }
    await queryClient.refetchQueries({queryKey: mergeBoxQueryKey}, {cancelRefetch: true})
  }

  function onAuthenticate() {
    if (!initiation || authenticationInFlightRef.current) return
    authenticationInFlightRef.current = true
    setIsAuthenticating(true)
    const controller = new AbortController()
    authenticationControllerRef.current = controller

    return authenticate(initiation, controller.signal).finally(() => {
      authenticationInFlightRef.current = false
      if (authenticationControllerRef.current === controller) authenticationControllerRef.current = null
      if (authenticationActiveRef.current) setIsAuthenticating(false)
    })
  }

  const authenticateButton = initiation ? (
    <button
      ref={authenticateButtonRef}
      data-proof-of-presence-authenticate={pullRequestPath}
      type="button"
      className="btn btn-sm"
      onClick={() => void onAuthenticate()}
      aria-disabled={isAuthenticating}
      aria-label={
        isAuthenticating
          ? 'Authenticating... Proof of presence, opens in a new tab'
          : 'Authenticate proof of presence, opens in a new tab'
      }
    >
      {isAuthenticating ? 'Authenticating...' : 'Authenticate'}
    </button>
  ) : undefined

  return (
    <MergeBoxSectionHeader
      headerId={headerId}
      title={summary ?? (isUnavailable ? 'Proof of presence unavailable' : 'Proof of presence required')}
      subtitle={
        <>
          {message ??
            (isUnavailable
              ? 'The proof of presence requirement could not be evaluated. Try again later.'
              : 'An authorized user must authenticate to merge.')}{' '}
          <Link href={DOCS_URL} target="_blank" rel="noopener noreferrer" inline>
            Learn more about proof of presence
          </Link>
        </>
      }
      icon={
        <CircleOcticon
          className={styles.unsatisfiedIcon}
          icon={() => (isUnavailable ? <AlertIcon size={16} /> : <ShieldIcon size={16} />)}
          size={HEADER_ICON_SIZE}
        />
      }
      rightSideContent={authenticateButton}
    />
  )
}

/**
 * Renders the enterprise proof of presence merge requirement.
 */
export const ProofOfPresenceSection = ({requirement}: ProofOfPresenceSectionProps) => {
  const {metadata, pullRequestPath} = requirement
  const {summary, message} = metadata.reason
  const headerId = useId()
  const sectionRef = useRef<HTMLElement>(null)
  const authenticateButtonRef = useRef<HTMLButtonElement>(null)
  const removedFocusRef = useRef<Element | null>(null)
  const previousRequirementRef = useRef({pullRequestPath, state: metadata.state})
  const onBeforeHeaderUnmount = useCallback(() => {
    const focusedElement = document.activeElement
    removedFocusRef.current = focusedElement && sectionRef.current?.contains(focusedElement) ? focusedElement : null
  }, [])

  useLayoutEffect(() => {
    const previous = previousRequirementRef.current
    previousRequirementRef.current = {pullRequestPath, state: metadata.state}
    const removedFocus = removedFocusRef.current
    removedFocusRef.current = null
    if (previous.pullRequestPath !== pullRequestPath) return

    let focusAnnouncedStatus = false
    const section = sectionRef.current
    if (removedFocus && !removedFocus.isConnected && document.activeElement === document.body && section) {
      const focusTarget =
        removedFocus instanceof HTMLButtonElement ? (authenticateButtonRef.current ?? section) : section
      focusTarget.focus({preventScroll: true})
      focusAnnouncedStatus = document.activeElement === section
    }
    // Focusing the named region already announces the new status.
    if (previous.state !== metadata.state && !focusAnnouncedStatus) {
      if (metadata.state === 'completed') {
        announce('Proof of presence verified')
      } else if (metadata.state === 'error') {
        announce(summary ?? 'Proof of presence unavailable')
      }
    }
  }, [pullRequestPath, metadata.state, summary, metadata.initiation?.action, metadata.initiation?.requiredLevel])

  useEffect(() => {
    if (metadata.state === 'completed') reportAuthenticationSuccess(pullRequestPath)
  }, [metadata.state, pullRequestPath])

  return (
    <section ref={sectionRef} tabIndex={-1} aria-labelledby={headerId} className="border-bottom color-border-subtle">
      {metadata.state === 'completed' ? (
        <VerifiedHeader verifiedBy={metadata.verifiedBy} headerId={headerId} />
      ) : (
        <RequiredHeader
          key={`${pullRequestPath}:${metadata.state}:${metadata.initiation?.action ?? ''}:${metadata.initiation?.requiredLevel ?? ''}`}
          summary={summary}
          message={message}
          initiation={metadata.state === 'pending' ? metadata.initiation : null}
          isUnavailable={metadata.state === 'error'}
          headerId={headerId}
          authenticateButtonRef={authenticateButtonRef}
          onBeforeUnmount={onBeforeHeaderUnmount}
          pullRequestPath={pullRequestPath}
        />
      )}
    </section>
  )
}
